import api from './api';

// ============================================
// PRODUTOS/SERVIÇOS (usado por Faturas e Contratos)
// ============================================

export interface ProdutoServico {
  id: number;
  tipo: 'produto' | 'servico';
  codigo?: string;
  nome: string;
  descricao?: string;
  categoria?: string;
  ncm?: string;
  valorUnit: number;
  precoMinimo?: number;
  unidade: string;
  destaque?: boolean;
  active: boolean;
}

// ============================================
// PROPOSTA
// ============================================

export interface Proposta {
  id: string;
  numero: number;
  tipo: 'vendas' | 'servicos';
  titulo?: string;
  status: 'rascunho' | 'enviada' | 'aprovada' | 'rejeitada';
  dataEmissao: string;
  dataValidade: string;
  dataEntregaPrevista?: string;
  briefing?: string;
  valorGlobal?: number;
  conteudo?: string;
  motivoRejeicao?: string;
  visibleInPortal: boolean;
  publicadoPortal: boolean;
  clientId: string;
  client: { id: string; razaoSocial: string; nomeFantasia?: string; cnpj?: string };
  criadoPor?: { id: string; name: string };
  createdAt: string;
}

export interface PropostaInput {
  clientId: string;
  titulo?: string;
  dataEmissao: string;
  dataValidade: string;
  dataEntregaPrevista?: string;
  briefing?: string;
  valorGlobal?: number;
  conteudo?: string;
}

export const propostaService = {
  async list(filters?: { clientId?: string; status?: string; page?: number; limit?: number }) {
    const params = new URLSearchParams();
    if (filters?.clientId) params.append('clientId', filters.clientId);
    if (filters?.status) params.append('status', filters.status);
    if (filters?.page) params.append('page', filters.page.toString());
    if (filters?.limit) params.append('limit', filters.limit.toString());

    const response = await api.get(`/propostas?${params}`);
    return response.data;
  },

  async getById(id: string): Promise<Proposta> {
    const response = await api.get(`/propostas/${id}`);
    return response.data;
  },

  async create(data: PropostaInput): Promise<Proposta> {
    const response = await api.post('/propostas', data);
    return response.data;
  },

  async update(id: string, data: PropostaInput): Promise<Proposta> {
    const response = await api.put(`/propostas/${id}`, data);
    return response.data;
  },

  async updateStatus(id: string, status: string, motivo?: string): Promise<Proposta> {
    const response = await api.patch(`/propostas/${id}/status`, { status, motivo });
    return response.data;
  },

  async delete(id: string): Promise<void> {
    await api.delete(`/propostas/${id}`);
  },

  async duplicate(id: string): Promise<Proposta> {
    const response = await api.post(`/propostas/${id}/duplicate`);
    return response.data;
  },

  async downloadPdf(id: string, numero?: number): Promise<void> {
    const response = await api.get(`/propostas/${id}/pdf`, { responseType: 'blob' });
    const url = URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
    const a = document.createElement('a');
    a.href = url;
    a.download = `proposta-${numero ? String(numero).padStart(3, '0') : id}.pdf`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  },
};

export const produtoServicoService = {
  async list(filters?: { tipo?: string; search?: string; active?: boolean }): Promise<ProdutoServico[]> {
    const params = new URLSearchParams();
    if (filters?.tipo) params.append('tipo', filters.tipo);
    if (filters?.search) params.append('search', filters.search);
    if (filters?.active !== undefined) params.append('active', filters.active.toString());

    const response = await api.get(`/propostas/produtos-servicos?${params}`);
    return response.data;
  },

  async getById(id: number): Promise<ProdutoServico> {
    const response = await api.get(`/propostas/produtos-servicos/${id}`);
    return response.data;
  },

  async create(data: Omit<ProdutoServico, 'id' | 'active'>): Promise<ProdutoServico> {
    const response = await api.post('/propostas/produtos-servicos', data);
    return response.data;
  },

  async update(id: number, data: Partial<ProdutoServico>): Promise<ProdutoServico> {
    const response = await api.put(`/propostas/produtos-servicos/${id}`, data);
    return response.data;
  },

  async delete(id: number): Promise<void> {
    await api.delete(`/propostas/produtos-servicos/${id}`);
  },
};
